import {
  QueryClient,
  type DefaultError,
  type QueryKey,
  type QueryObserverOptions,
} from '@tanstack/react-query'

import { ApiError, ApiParseError, isCancelledError } from '@/lib/api/errors'

type LoaderQueryOptions<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = Pick<
  QueryObserverOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey>,
  'queryKey' | 'queryFn' | 'enabled' | 'staleTime' | 'gcTime' | 'retry' | 'retryDelay'
> &
  Record<string, unknown>

/** TanStack Query v5.102+ replacement for deprecated `queryClient.ensureQueryData`. */
export function ensureQueryData<
  TQueryFnData,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  client: QueryClient,
  options: LoaderQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): Promise<TData> {
  return (async () => {
    try {
      return await client.query({ ...options, staleTime: 'static' })
    } catch (error) {
      if (!isCancelledError(error)) throw error
      const cached = client.getQueryData<TData>(options.queryKey)
      if (cached !== undefined) return cached
      throw error
    }
  })()
}

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        // Mobile users switch apps constantly; refetching on every return is noisy.
        refetchOnWindowFocus: false,
        retry(failureCount, error) {
          if (isCancelledError(error)) return false
          if (error instanceof ApiParseError) return false
          if (error instanceof ApiError && !error.isRetryable) return false
          return failureCount < 2
        },
      },
      mutations: {
        retry: false,
      },
    },
  })
}
